import { z } from 'zod';

const booleanFromEnv = (defaultValue: boolean) =>
  z.preprocess(value => {
    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (['true', '1', 'yes', 'on'].includes(normalized)) {
        return true;
      }

      if (['false', '0', 'no', 'off', ''].includes(normalized)) {
        return false;
      }
    }

    if (typeof value === 'number') {
      return value !== 0;
    }

    return value;
  }, z.boolean().default(defaultValue));

// Environment validation schema for frontend
const envSchema = z.object({
  // API Configuration
  VITE_API_BASE_URL: z.string().url().default('http://localhost:8000'),
  VITE_AUTH_BASE_URL: z.string().url().default('http://localhost:3001'),
  VITE_API_TIMEOUT: z.coerce.number().default(30000),

  // Application Configuration
  VITE_APP_NAME: z.string().default('Delivery Metrics Dashboard'),
  VITE_APP_VERSION: z.string().default('1.0.0'),
  VITE_APP_DESCRIPTION: z
    .string()
    .default('Software Delivery Metrics Dashboard'),

  // Feature Flags
  VITE_ENABLE_MOCK_DATA: booleanFromEnv(false),
  VITE_ENABLE_DEBUG_MODE: booleanFromEnv(false),
  VITE_ENABLE_PERFORMANCE_MONITORING: booleanFromEnv(true),

  // Authentication
  VITE_AUTH_PROVIDER: z
    .enum(['local', 'saml', 'oauth', 'entra-id'])
    .default('local'),
  VITE_SAML_ENABLED: booleanFromEnv(false),
  VITE_OAUTH_ENABLED: booleanFromEnv(false),
  VITE_SSO_ENABLED: booleanFromEnv(false),

  // Dashboard Configuration
  VITE_DEFAULT_REFRESH_INTERVAL: z.coerce.number().default(300000), // 5 minutes
  VITE_MAX_CHART_DATA_POINTS: z.coerce.number().default(1000),
  VITE_ENABLE_REAL_TIME_UPDATES: booleanFromEnv(false),
  VITE_SHOW_DEVELOPER_DASHBOARD: booleanFromEnv(false),

  // External Services
  VITE_ANALYTICS_ENABLED: booleanFromEnv(false),
  VITE_ANALYTICS_ID: z.string().optional(),

  // Development Tools
  VITE_ENABLE_REACT_DEVTOOLS: booleanFromEnv(true),
  VITE_ENABLE_QUERY_DEVTOOLS: booleanFromEnv(true),

  // Collector Stats ( load report ) - to know its STAGING or PRODUCTION and load runs accoirdingly
   VITE_ENVIRONMENT: z.enum(['staging', 'production']).default('production'),
});

// Parse and validate environment variables
const parseEnv = () => {
  try {
    // Get all environment variables that start with VITE_
    const viteEnv = Object.keys(import.meta.env)
      .filter(key => key.startsWith('VITE_'))
      .reduce(
        (acc, key) => {
          acc[key] = import.meta.env[key];
          return acc;
        },
        {} as Record<string, any>
      );

    return envSchema.parse(viteEnv);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const invalidVars = error.errors.map(
        err => `${err.path.join('.')}: ${err.message}`
      );
      console.error('Frontend environment validation failed:', invalidVars);

      // In development, we can be more lenient and use defaults
      if (import.meta.env.DEV) {
        console.warn(
          'Using default values for invalid environment variables in development mode'
        );
        return envSchema.parse({});
      }

      throw new Error(
        `Frontend environment validation failed: ${invalidVars.join(', ')}`
      );
    }
    throw error;
  }
};

// Export validated environment configuration
export const env = parseEnv();

// Type for environment configuration
export type FrontendEnvConfig = typeof env;

// Helper functions
export const isDevelopment = () => import.meta.env.DEV;
export const isProduction = () => import.meta.env.PROD;

// API configuration helpers
export const getApiUrl = (path: string = '') => {
  const baseUrl = env.VITE_API_BASE_URL.replace(/\/$/, '');
  const cleanPath = path.replace(/^\//, '');
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
};

export const getAuthUrl = (path: string = '') => {
  const baseUrl = env.VITE_AUTH_BASE_URL.replace(/\/$/, '');
  const cleanPath = path.replace(/^\//, '');
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
};

// Feature flag helpers
export const isFeatureEnabled = (
  feature: keyof Pick<
    FrontendEnvConfig,
    | 'VITE_ENABLE_MOCK_DATA'
    | 'VITE_ENABLE_DEBUG_MODE'
    | 'VITE_ENABLE_PERFORMANCE_MONITORING'
    | 'VITE_SAML_ENABLED'
    | 'VITE_OAUTH_ENABLED'
    | 'VITE_SSO_ENABLED'
    | 'VITE_ENABLE_REAL_TIME_UPDATES'
    | 'VITE_SHOW_DEVELOPER_DASHBOARD'
    | 'VITE_ANALYTICS_ENABLED'
    | 'VITE_ENABLE_REACT_DEVTOOLS'
    | 'VITE_ENABLE_QUERY_DEVTOOLS'
  >
) => {
  return env[feature];
};

// Application metadata
export const getAppMetadata = () => ({
  name: env.VITE_APP_NAME,
  version: env.VITE_APP_VERSION,
  description: env.VITE_APP_DESCRIPTION,
});

// Dashboard configuration
export const getDashboardConfig = () => ({
  refreshInterval: env.VITE_DEFAULT_REFRESH_INTERVAL,
  maxChartDataPoints: env.VITE_MAX_CHART_DATA_POINTS,
  realTimeUpdates: env.VITE_ENABLE_REAL_TIME_UPDATES,
});

// Development tools configuration
export const getDevToolsConfig = () => ({
  mockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  showDevTools: import.meta.env.DEV,
});
